import React, { useState, useEffect, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import { constants, logEducatorDashboard, LogLevel } from "utils";
import { trackClickedButton } from "utils/analytics";
import { request } from "hooks/useOkoApi/useOkoApi";
import { useCurriculumClear } from "hooks/useCurriculumClear/useCurriculumClear";
import { compareCode, signature, toDisplayTrack, countDistribution } from "./assignmentDraft";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";
import { FocusedEditHeader } from "./FocusedEditHeader";
import { patchGroupAssignments, applyQuestionSet } from "hooks/useCurriculum/useCurriculum";
import { useSessionLength, DEFAULT_SESSION_LENGTH } from "./useSessionLength";
import { AssignmentAccordion } from "./AssignmentAccordion";
import { oneListCardProps } from "./oneListCards";
import { UnassignedChip } from "./UnassignedCodesRail";
import { ClearGroupCodesModal } from "./ClearGroupCodesModal";
import { ViewQuestionsModal } from "./ViewQuestionsModal";
import { resolveGroupCardTitle } from "utils/groupCardTitle";
import { ReuseFilterModal, reuseFilterCandidates } from "./ReuseFilterModal";
import { ArchiveLessonModal } from "./ArchiveLessonModal";
import { CopyLessonModal } from "./CopyLessonModal";

// Standard and lesson bundles sit side by side in one class and share button
// names, so every event from each view says which kind it came from.
const ASSIGNMENT_TYPE = "Standard";

// Renders a single STANDARD (differentiation) bundle inside the LessonGroupsList
// flow. The unified group-assignments read returns standard bundles alongside
// lesson bundles; this mirrors LessonGroupsView's inline editor for a standard
// bundle — reassign/unassign codes and save — persisting via the differentiation
// (groups/) endpoints, keeping "Edit Lesson" the same inline behavior the
// differentiation dashboard already has. Only the bundle's primary standard has a
// name in the unified read, so prereq/extension chips fall back to their code.
const buildDraft = (assignmentId, standard, assignments, lastUpdatedAt = null, poolCodes = []) => {
  if (!standard || !assignments) return { bundle: null, unassigned: [] };
  const primaryCode = standard.standard_code || "—";
  const primaryName = standard.standard_name || standard.standard_code || "—";
  const bundle = {
    assignment_id: assignmentId,
    bundle_type: "standard",
    primary_standard_code: primaryCode,
    primary_standard_name: primaryName,
    last_updated_at: lastUpdatedAt,
    assignments: assignments
      .filter((a) => a.track_type && a.track_type !== "no_assignment")
      .map((a) => ({
        group_code: a.group_code,
        standard_code: a.standard_code,
        standard_codes: a.standard_codes,
        // The assignment carries its own name; prereq/extension codes differ from the
        // bundle's primary, so keying off primaryName alone left them titled by code.
        standard_name: a.standard_name
          || (a.standard_code === primaryCode ? primaryName : null)
          || a.standard_code
          || "—",
        track_type: toDisplayTrack(a.track_type),
        // Advanced Selection filter — drives the card's "Customized"
        // tag and the filtered View Questions preview.
        question_filter: a.question_filter,
        // Size of the set the group draws from, already filter-aware server-side.
        // This map is a whitelist, so a field absent here never reaches the card.
        question_count: a.question_count,
        // Saved-set pointer: labels the card and switches the preview modal to its
        // saved-set variant, same as the lesson flow.
        question_set_id: a.question_set_id,
        learning_components: a.learning_components,
      })),
  };
  // Unassigned = the classroom-wide free pool (single source for the count and
  // the Add Group Code dropdown), unioned with this bundle's not-yet-released codes.
  const bundleFree = assignments.filter((a) => a.track_type === "no_assignment").map((a) => a.group_code);
  const unassigned = [...new Set([...(poolCodes || []), ...bundleFree])].sort(compareCode);
  return { bundle, unassigned };
};

export const StandardGroupsView = ({
  standard,
  assignments,
  assignmentId,
  lastUpdatedAt = null,
  classroomId,
  selectedClassId,
  poolCodes,
  poolTotal,
  onRefresh,
  // Copy Standard: the account's classes (with per-class assignment_summary) feed
  // the target list; onLessonCopied hands the response's copies up for the success
  // banners. The kebab only renders when onLessonCopied is wired, same as lessons.
  classes,
  onLessonCopied,
  onAccountRefresh,
  // Focused edit mode: the list hides its chrome and the other bundles while one is
  // being edited, so it needs to know when this view enters and leaves edit mode.
  onEditingChange,
  // Set on Manage Groups as one list: Figma's card, expanded as the list says.
  listCard,
}) => {
  const history = useHistory();
  const { sessionLength, onSessionLengthChange } = useSessionLength(assignments, "StandardGroupsView");
  const [isEditing, setIsEditingState] = useState(false);
  const setIsEditing = (next) => {
    setIsEditingState(next);
    onEditingChange?.(next);
  };
  const [saving, setSaving] = useState(false);
  const [activeDragCode, setActiveDragCode] = useState(null);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  // Classroom-wide clear (every bundle's codes; released immediately on confirm).
  const { clearableCodes, clearCodes } = useCurriculumClear(classroomId, isClearModalOpen);
  const [viewQuestionsTarget, setViewQuestionsTarget] = useState(null);
  const [reuseOffer, setReuseOffer] = useState(null);
  const [savingQuestionFilter, setSavingQuestionFilter] = useState(false);
  const [questionFilterSaveError, setQuestionFilterSaveError] = useState(null);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const archivingRef = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  const serverDraft = useMemo(() => buildDraft(assignmentId, standard, assignments, lastUpdatedAt, poolCodes), [assignmentId, standard, assignments, lastUpdatedAt, poolCodes]);
  const [draft, setDraft] = useState(serverDraft);
  useEffect(() => { setDraft(serverDraft); }, [serverDraft]);

  const isDirty = useMemo(() => {
    if (!draft.bundle || !serverDraft.bundle) return false;
    return signature([draft.bundle], draft.unassigned, null) !== signature([serverDraft.bundle], serverDraft.unassigned, null);
  }, [draft, serverDraft]);

  const guardDirty = () => {
    if (!isDirty) return true;
    // eslint-disable-next-line no-alert
    return window.confirm("You have unsaved changes that will be lost. Continue?");
  };

  // Navigate to the standard Edit Group Focus page to (re)assign a code's track /
  // standard — reuses the differentiation screen.
  const goToStandardFocus = (code) => {
    if (!guardDirty()) return;
    const params = new URLSearchParams({
      classId: selectedClassId || "",
      classroomId: classroomId || "",
      assignmentId: assignmentId || "",
      groupCode: code || "",
    });
    history.push(`${constants.EDIT_GROUP_FOCUS_PATH}?${params.toString()}`);
  };

  // Into the Question Set view for one group (from the card link or the preview
  // modal's "Create My Question Set" CTA). The page resolves the bundle out of the
  // unified envelope and scopes by standard when the bundle carries no lesson, so
  // the same params serve both bundle kinds.
  const goToQuestionSet = (code, source) => {
    if (!guardDirty()) return;
    trackClickedButton("Edit Question Set", {
      page_name: "Dashboard",
      assignment_type: ASSIGNMENT_TYPE,
      source,
      class: classroomId || null,
      group_code: code || null,
    });
    const qsParams = new URLSearchParams({
      classId: selectedClassId || "",
      classroomId: classroomId || "",
      assignmentId: assignmentId || "",
      groupCode: code,
    });
    history.push(`${constants.QUESTION_SET_PATH}?${qsParams.toString()}`);
  };

  // Change the bundle's standard (swap + redistribute) via the proven page.
  const onChangeStandard = () => {
    if (!guardDirty()) return;
    trackClickedButton("Change Standard", {
      page_name: "Dashboard",
      assignment_type: ASSIGNMENT_TYPE,
      source: "standard_groups_view",
      class: classroomId || null,
      assignment_id: assignmentId || null,
    });
    // Route to the unified change view (ViewChangeLesson) so the curriculum flow
    // gets the Lesson/Standard toggle; currentStandardCode opens it in Standard
    // mode. Differentiation still uses ViewChangeStandard (no toggle).
    const bundleCodes = (assignments || []).map((a) => a.group_code);
    // Current per-track distribution — seeds the change modal (clamped to the new
    // lesson/standard's available tracks).
    const dist = countDistribution(assignments);
    // Distributable = classroom pool + this bundle's own codes (freed on change),
    // bundle codes first. Threaded whole so the commit can realize the distribution
    // over the actual selected codes and grow the bundle past its current codes.
    const availableCodes = [...new Set([...bundleCodes, ...(poolCodes || [])])];
    const params = new URLSearchParams({
      classId: selectedClassId || "",
      classroomId: classroomId || "",
      assignmentId: assignmentId || "",
      currentStandardCode: standard?.standard_code || "",
      groupCodes: bundleCodes.join(","),
      availableCodes: availableCodes.join(","),
      totalAvailable: String(availableCodes.length),
      initCore: String(dist.core),
      initPrereq: String(dist.prerequisites),
      initExt: String(dist.extensions),
    });
    history.push(`${constants.VIEW_CHANGE_LESSON_PATH}?${params.toString()}`);
  };

  // ---- Draft mutations (local only; persisted on Save) ----
  const handleTrash = (code) => {
    setDraft((prev) => ({
      bundle: { ...prev.bundle, assignments: prev.bundle.assignments.filter((a) => a.group_code !== code) },
      unassigned: prev.unassigned.includes(code) ? prev.unassigned : [...prev.unassigned, code].sort(compareCode),
    }));
  };

  // Clear frees codes classroom-wide (any bundle) immediately on confirm, then we
  // reload so the pool + cards reflect it — and re-enter edit mode so the educator
  // can keep reassigning the freed codes without re-opening the editor.
  const handleClearConfirm = async (selection) => {
    setIsClearModalOpen(false);
    try {
      await clearCodes(selection);
      await onRefresh?.();
      setIsEditing(true);
    } catch (e) {
      logEducatorDashboard("StandardGroupsView: failed to clear group codes", { level: LogLevel.WARNING });
    }
  };


  // Delta via the differentiation PUT: send only changed codes so unchanged ones
  // on the bundle are never re-sent / re-validated against the poaching guard.
  // Duplicate = clone the card's row onto the first free code as a draft entry and
  // enter edit mode, so the teacher reviews before the normal Save delta commits it.
  const handleDuplicateCard = (card) => {
    setDraft((prev) => {
      const free = prev.unassigned[0];
      if (!free) return prev;
      return {
        bundle: { ...prev.bundle, assignments: [...prev.bundle.assignments, { ...card, group_code: free }] },
        unassigned: prev.unassigned.filter((c) => c !== free),
      };
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!isDirty) { setIsEditing(false); return; }
    setSaving(true);
    try {
      const serverByCode = new Map((serverDraft.bundle?.assignments || []).map((a) => [a.group_code, a]));
      const draftByCode = new Map(draft.bundle.assignments.map((a) => [a.group_code, a]));
      const changes = [];
      for (const a of draft.bundle.assignments) {
        const prev = serverByCode.get(a.group_code);
        if (!prev || prev.standard_code !== a.standard_code || prev.track_type !== a.track_type) {
          changes.push({ group_code: a.group_code, track_type: a.track_type, standard_code: a.standard_code });
        }
      }
      for (const a of (serverDraft.bundle?.assignments || [])) {
        if (!draftByCode.has(a.group_code)) {
          changes.push({ group_code: a.group_code, track_type: null, standard_code: null });
        }
      }
      if (changes.length > 0) {
        await request(`groups/classrooms/${classroomId}/assignments/${assignmentId}`, { method: "PUT", payload: { assignments: changes } });
      }

      // The update body has no set pointer, so a duplicated card would land on the
      // full bank. Point the new code at the source group's set once it exists.
      const newSetTargets = draft.bundle.assignments
        .filter((a) => a.question_set_id && !serverByCode.has(a.group_code))
        .reduce((acc, a) => {
          (acc[a.question_set_id] = acc[a.question_set_id] || []).push(a.group_code);
          return acc;
        }, {});
      await Promise.all(Object.entries(newSetTargets)
        .map(([setId, codes]) => applyQuestionSet(setId, codes)));
      trackClickedButton("Save Group Assignments", {
        page_name: "Dashboard",
        assignment_type: ASSIGNMENT_TYPE,
        source: "standard_groups_view",
        class: classroomId || null,
      });
      await onRefresh?.();
      setIsEditing(false);
    } catch (e) {
      logEducatorDashboard("StandardGroupsView: failed to save assignments", {
        level: LogLevel.WARNING,
        event: "save_error",
        statusCode: e?.status,
        classroomId,
        assignmentId,
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmArchive = async () => {
    if (!classroomId || !assignmentId || archivingRef.current) return;
    archivingRef.current = true;
    setArchiving(true);
    setIsArchiveModalOpen(false);
    try {
      await request(`groups/classrooms/${classroomId}/assignments/${assignmentId}`, { method: "DELETE" });
      trackClickedButton("Archive Lesson", {
        page_name: "Dashboard",
        assignment_type: ASSIGNMENT_TYPE,
        source: "standard_groups_view",
        class: classroomId || null,
      });
      listCard?.onArchived?.();
    } catch (e) {
      if (e?.status === 404) listCard?.onArchived?.();
      else {
        logEducatorDashboard("StandardGroupsView: failed to archive bundle", {
          level: LogLevel.WARNING,
          event: "archive_error",
          statusCode: e?.status,
          classroomId,
          assignmentId,
        });
      }
    } finally {
      await onRefresh?.();
      archivingRef.current = false;
      setArchiving(false);
    }
  };

  // ---- Drag handling (drop an unassigned code onto the bundle → focus page) ----
  const onDragStart = (e) => setActiveDragCode(e.active?.data?.current?.code || null);
  const onDragCancel = () => setActiveDragCode(null);
  const onDragEnd = (e) => {
    setActiveDragCode(null);
    const code = e.active?.data?.current?.code;
    const overData = e.over?.data?.current;
    if (!code || !overData?.assignmentId) return;
    goToStandardFocus(code);
  };

  if (!standard || !draft.bundle) return null;

  // draft.unassigned is the classroom pool, so the count and the Add Group Code
  // dropdown read the same set (consistent with the list header).
  const availableCount = draft.unassigned.length;
  const totalGroups = typeof poolTotal === "number" ? poolTotal : draft.bundle.assignments.length + draft.unassigned.length;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <div>
        {isEditing && (
          <FocusedEditHeader
            className={classDisplayName(classes?.find((c) => c.id === selectedClassId))}
            availableCount={availableCount}
            totalGroups={totalGroups}
            onClear={totalGroups > availableCount ? () => setIsClearModalOpen(true) : undefined}
          />
        )}

        <AssignmentAccordion
          {...oneListCardProps(listCard)}
          bundle={draft.bundle}
          draftAssignments={draft.bundle.assignments}
          unassignedCodes={draft.unassigned}
          sessionLength={sessionLength ?? DEFAULT_SESSION_LENGTH}
          sessionLengthEditable
          keepMetaWhileEditing
          onSessionLengthChange={onSessionLengthChange}
          isEditing={isEditing}
          onEnterEdit={() => setIsEditing(true)}
          onSave={handleSave}
          saving={saving}
          isDirty={isDirty}
          onArchive={classroomId ? () => setIsArchiveModalOpen(true) : undefined}
          onCopyLesson={onLessonCopied ? () => setIsCopyModalOpen(true) : undefined}
          onChangeLessonFocus={onChangeStandard}
          onAddCode={goToStandardFocus}
          onTrashCode={handleTrash}
          onDuplicateCard={handleDuplicateCard}
          onEditCardStandard={goToStandardFocus}
          onViewQuestions={(card) => setViewQuestionsTarget({
            code: card.standard_code,
            name: card.standard_name,
            questionFilter: card.question_filter,
            // Every standard row pins its own code — there is no lesson-wide Core
            // span to fall back to, so the modal always scopes by standard.
            trackType: card.track_type,
            groupCode: card.group_code,
            questionSetId: card.question_set_id || null,
            focus: resolveGroupCardTitle(card, draft.bundle),
          })}
        />
      </div>

      <DragOverlay>{activeDragCode ? <UnassignedChip code={activeDragCode} dragging /> : null}</DragOverlay>

      <ClearGroupCodesModal
        isOpen={isClearModalOpen}
        onClose={() => setIsClearModalOpen(false)}
        clearable={clearableCodes}
        onConfirm={handleClearConfirm}
      />

      <ViewQuestionsModal
        isOpen={Boolean(viewQuestionsTarget)}
        onClose={() => { setViewQuestionsTarget(null); setQuestionFilterSaveError(null); }}
        standardCode={viewQuestionsTarget?.code}
        standardName={viewQuestionsTarget?.name}
        questionFilter={viewQuestionsTarget?.questionFilter}
        questionSetId={viewQuestionsTarget?.questionSetId}
        groupCode={viewQuestionsTarget?.groupCode}
        focus={viewQuestionsTarget?.focus}
        editable
        saving={savingQuestionFilter}
        saveError={questionFilterSaveError}
        onCreateQuestionSet={() => {
          const code = viewQuestionsTarget?.groupCode;
          if (!code) return;
          setViewQuestionsTarget(null);
          goToQuestionSet(code, "view_questions_modal");
        }}
        onSave={async (nextQuestionFilter, filteredTotal) => {
          const target = viewQuestionsTarget;
          if (!target) return;
          setSavingQuestionFilter(true);
          setQuestionFilterSaveError(null);
          try {
            // Same delta contract as the lesson flow, but the track spelling is
            // per bundle kind: standard bundles keep the plural "extensions" the
            // rows are stored with. Only lesson bundles map to the singular form.
            await patchGroupAssignments(classroomId, assignmentId, [{
              group_code: target.groupCode,
              track_type: target.trackType,
              standard_code: target.code,
              question_filter: nextQuestionFilter,
            }]);
            await onRefresh?.();
            setViewQuestionsTarget(null);
            // Other groups on this topic get the same offer the My Question Set
            // flow makes after a save (OKO2-3803).
            const candidates = reuseFilterCandidates(serverDraft.bundle?.assignments, {
              groupCode: target.groupCode,
              trackType: target.trackType,
              standardCode: target.code,
            });
            if (candidates.length > 0) {
              setReuseOffer({
                groupCode: target.groupCode,
                trackType: target.trackType,
                standardCode: target.code,
                standardName: target.name,
                filter: nextQuestionFilter,
                total: filteredTotal,
                candidates,
              });
            }
          } catch (e) {
            logEducatorDashboard("StandardGroupsView: failed to save question filter", {
              level: LogLevel.WARNING,
              event: "question_filter_save_error",
              statusCode: e?.status,
              groupCode: target.groupCode,
            });
            setQuestionFilterSaveError(e?.detail || e?.message || "Failed to save filters.");
          } finally {
            setSavingQuestionFilter(false);
          }
        }}
      />

      <ReuseFilterModal
        offer={reuseOffer}
        bundle={serverDraft.bundle || {}}
        classroomId={classroomId}
        assignmentId={assignmentId}
        onClose={() => setReuseOffer(null)}
        onApplied={onRefresh}
      />

      <CopyLessonModal
        assignmentType={ASSIGNMENT_TYPE}
        isOpen={isCopyModalOpen}
        onClose={() => setIsCopyModalOpen(false)}
        classroomId={classroomId}
        assignmentId={assignmentId}
        lessonLabel={standard?.standard_code}
        lessonName={standard?.standard_name || standard?.standard_code}
        classes={classes}
        // One free code needed per real-track group in this bundle (the
        // no_assignment rows are already filtered out of the built bundle).
        requiredCount={serverDraft.bundle?.assignments.length || 0}
        onCopied={(copies) => {
          setIsCopyModalOpen(false);
          onLessonCopied?.(copies, standard?.standard_name || standard?.standard_code);
        }}
        onAccountRefresh={onAccountRefresh}
      />

      <ArchiveLessonModal
        isOpen={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
        onConfirm={confirmArchive}
        archiving={archiving}
      />
    </DndContext>
  );
};
